// RestaurantLoader.tsx
import { useEffect, useState } from "react";
import './RestaurantLoader.scss';
import { useConfig } from "../../../context/ConfigContext";

interface LoaderProps {
  message?: string;
  size?: 'small' | 'medium' | 'large';
  fullScreen?: boolean;
  variant?: 'spinner' | 'dots' | 'pulse' | 'plate';
  primaryColor?: string;
  logo?: string;
}

export default function RestaurantLoader({
  message = 'Loading',
  size = 'medium',
  fullScreen = false,
  variant = 'spinner',
}: LoaderProps) {
  const { config } = useConfig();
  const primaryColor = config?.primaryColor || '#FF6B35'
  const logo = config?.appLogoImage || '/placeholder-logo.png';
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const renderLoader = () => {
    switch (variant) {
      case 'spinner':
        return (
          <div className="loader-spinner" data-size={size}>
            <div
              className="spinner-ring"
              style={{ borderTopColor: primaryColor }}
            />
            {logo && (
              <img
                src={logo}
                alt="Logo"
                className="spinner-logo"
              />
            )}
          </div>
        );

      case 'dots':
        return (
          <div className="loader-dots" data-size={size}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="dot"
                style={{
                  backgroundColor: primaryColor,
                  animationDelay: `${i * 0.16}s`
                }}
              />
            ))}
          </div>
        );

      case 'pulse':
        return (
          <div className="loader-pulse" data-size={size}>
            <div
              className="pulse-ring"
              style={{ backgroundColor: primaryColor }}
            />
            <div
              className="pulse-core"
              style={{ backgroundColor: primaryColor }}
            />
            {logo && (
              <img
                src={logo}
                alt="Logo"
                className="pulse-logo"
              />
            )}
          </div>
        );

      case 'plate':
        return (
          <div className="loader-plate" data-size={size}>
            <div className="plate" style={{ borderColor: primaryColor }}>
              {logo ? (
                <img src={logo} alt="Logo" className="plate-logo" />
              ) : (
                <div className="plate-content" style={{ backgroundColor: primaryColor }}>
                  <div className="fork" style={{ borderRightColor: primaryColor }} />
                  <div className="knife" style={{ backgroundColor: primaryColor }} />
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`restaurant-loader ${fullScreen ? 'fullscreen' : ''}`}>
      <div className="loader-container">
        {renderLoader()}
        {message && (
          <p
            className={`loader-message ${size}`}
            style={{ color: primaryColor }}
          >
            {message}{dots}
          </p>
        )}
      </div>
    </div>
  );
}

// RestaurantLoader.scss
/*
.restaurant-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  &.fullscreen {
    position: fixed;
    inset: 0;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(4px);
    z-index: 9999;
  }

  .loader-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }

  .loader-message {
    font-weight: 500;
    margin: 0;

    &.small { font-size: 0.875rem; }
    &.medium { font-size: 1rem; }
    &.large { font-size: 1.25rem; }
  }
}

// Spinner Loader
.loader-spinner {
  position: relative;

  .spinner-ring {
    border: 4px solid #e5e7eb;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .spinner-logo {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 50%;
    height: 50%;
    object-fit: contain;
  }

  &[data-size="small"] .spinner-ring { width: 2rem; height: 2rem; }
  &[data-size="medium"] .spinner-ring { width: 4rem; height: 4rem; }
  &[data-size="large"] .spinner-ring { width: 6rem; height: 6rem; }
}

// Dots Loader
.loader-dots {
  display: flex;
  gap: 0.5rem;

  .dot {
    border-radius: 50%;
    animation: bounce 1.4s ease-in-out infinite both;
  }

  &[data-size="small"] .dot { width: 0.5rem; height: 0.5rem; }
  &[data-size="medium"] .dot { width: 0.75rem; height: 0.75rem; }
  &[data-size="large"] .dot { width: 1rem; height: 1rem; }
}

// Pulse Loader
.loader-pulse {
  position: relative;

  .pulse-ring {
    border-radius: 50%;
    opacity: 0.75;
    animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  .pulse-core {
    position: absolute;
    inset: 0;
    border-radius: 50%;
  }

  .pulse-logo {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 50%;
    height: 50%;
    object-fit: contain;
    z-index: 1;
  }

  &[data-size="small"] .pulse-ring,
  &[data-size="small"] .pulse-core { width: 2rem; height: 2rem; }
  &[data-size="medium"] .pulse-ring,
  &[data-size="medium"] .pulse-core { width: 4rem; height: 4rem; }
  &[data-size="large"] .pulse-ring,
  &[data-size="large"] .pulse-core { width: 6rem; height: 6rem; }
}

// Plate Loader (Restaurant themed)
.loader-plate {
  .plate {
    border: 3px solid;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: rotate 2s linear infinite;
    position: relative;
  }

  .plate-logo {
    width: 60%;
    height: 60%;
    object-fit: contain;
  }

  .plate-content {
    width: 70%;
    height: 70%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    opacity: 0.2;
  }

  .fork {
    width: 0.25rem;
    height: 1.5rem;
    background: transparent;
    border-right: 2px solid;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -0.125rem;
      width: 0.5rem;
      height: 0.5rem;
      border-top: 2px solid;
      border-left: 2px solid;
      border-right: 2px solid;
    }
  }

  .knife {
    width: 0.25rem;
    height: 1.5rem;
    border-radius: 2px;
  }

  &[data-size="small"] .plate { width: 3rem; height: 3rem; }
  &[data-size="medium"] .plate { width: 5rem; height: 5rem; }
  &[data-size="large"] .plate { width: 7rem; height: 7rem; }
}

// Animations
@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

@keyframes rotate {
  to { transform: rotate(360deg); }
}
*/